/**
 * The game client's compressed sprite package and bounded decoded-image cache.
 * Atlas paths remain unchanged: the renderer still receives Image elements.
 * The package is stored through the browser Cache API; decoded pixels are kept
 * only in this process and evicted least-recently-used above the budget.
 */
const PACK_META = "/sprites.pack.json";
const CACHE_NAME = "pd-sprite-pack-v1";
const DECODED_BUDGET = 256 * 1024 * 1024;
// Firefox on macOS has a native socket descriptor ceiling. Thousands of
// simultaneous blob-backed Image elements can drive its process past it even
// when their decoded pixels fit the byte budget.
const IMAGE_LIMIT = 1024;

type PackRow = [path: string, offset: number, length: number];
type PackMeta = { hash: string; bytes: number; files: number };
type ImageEntry = {
  image: HTMLImageElement;
  url: string;
  ready: Promise<void>;
  bytes: number;
  used: number;
};

let storeGeneration = 0;

export class SpriteStore {
  private images = new Map<string, ImageEntry>();
  private pins = new Set<string>();
  private decodedBytes = 0;
  private clock = 0;
  /** Identity of THIS package. Art is cached elsewhere in the renderer keyed by
   *  path; a reinstalled package (a reload picking up a new build) has the same
   *  paths and different bytes, so every derived cache must miss. Bumped per
   *  installed store, never reused. */
  readonly generation = ++storeGeneration;

  constructor(private blob: Blob, private rows: Map<string, [number, number]>, private dataStart: number) {}

  has(path: string): boolean { return this.rows.has(path); }

  async text(path: string): Promise<string> {
    const row = this.rows.get(path);
    if (!row) throw new Error(`sprite package has no ${path}`);
    return this.blob.slice(this.dataStart + row[0], this.dataStart + row[0] + row[1]).text();
  }

  image(path: string): HTMLImageElement {
    const old = this.images.get(path);
    if (old) {
      old.used = ++this.clock;
      return old.image;
    }
    const row = this.rows.get(path);
    if (!row) throw new Error(`sprite package has no ${path}`);
    const url = URL.createObjectURL(this.blob.slice(this.dataStart + row[0], this.dataStart + row[0] + row[1], "image/png"));
    const image = new Image();
    const entry: ImageEntry = { image, url, ready: Promise.resolve(), bytes: 0, used: ++this.clock };
    this.images.set(path, entry);
    image.src = url;
    entry.ready = image.decode().then(() => {
      entry.bytes = image.naturalWidth * image.naturalHeight * 4;
      this.decodedBytes += entry.bytes;
      this.evict();
    });
    // On-demand rendering starts loads without awaiting them. Keep its
    // rejection handled; explicit preload still observes the same promise.
    void entry.ready.catch(() => {});
    return image;
  }

  url(path: string): string {
    this.pins.add(path);
    this.image(path);
    return this.images.get(path)!.url;
  }

  pin(paths: Iterable<string>): void {
    for (const path of paths) this.pins.add(path);
  }

  /** Bounded concurrency avoids creating thousands of pending decoders. */
  async preload(paths: Iterable<string>, progress?: (done: number, total: number) => void): Promise<void> {
    const unique = [...new Set(paths)];
    let cursor = 0;
    let done = 0;
    const worker = async (): Promise<void> => {
      while (cursor < unique.length) {
        const path = unique[cursor++]!;
        this.image(path);
        await this.images.get(path)!.ready;
        progress?.(++done, unique.length);
      }
    };
    await Promise.all(Array.from({ length: Math.min(8, unique.length) }, worker));
  }

  private evict(): void {
    while (this.decodedBytes > DECODED_BUDGET || this.images.size > IMAGE_LIMIT) {
      let victimPath: string | null = null;
      let oldest = Number.POSITIVE_INFINITY;
      for (const [path, entry] of this.images) {
        if (entry.bytes === 0 || this.pins.has(path) || entry.used >= oldest) continue;
        oldest = entry.used;
        victimPath = path;
      }
      if (!victimPath) break; // the active scene alone exceeds the budget
      const victim = this.images.get(victimPath)!;
      this.images.delete(victimPath);
      this.decodedBytes -= victim.bytes;
      victim.image.src = "";
      URL.revokeObjectURL(victim.url);
    }
  }
}

let activeStore: SpriteStore | null = null;
export function spriteStore(): SpriteStore | null { return activeStore; }

async function loadBlob(meta: PackMeta, progress?: (stage: string) => void): Promise<Blob> {
  const url = `/sprites.pack?v=${meta.hash}`;
  let cache: Cache | null = null;
  try { cache = await caches.open(CACHE_NAME); } catch { /* private mode */ }
  let response = await cache?.match(url);
  if (response) progress?.("Reading stored art…");
  if (!response) {
    progress?.("Downloading art…");
    response = await fetch(url, { cache: "no-store" });
    if (!response.ok) throw new Error(`sprite package: HTTP ${response.status}`);
    const persist = cache?.put(url, response.clone()).catch(() => undefined);
    const blob = await response.blob();
    await persist;
    if (cache) {
      for (const key of await cache.keys()) if (key.url !== new URL(url, location.href).href) await cache.delete(key);
    }
    return blob;
  }
  return response.blob();
}

export async function loadSpriteStore(progress?: (stage: string) => void): Promise<SpriteStore> {
  if (activeStore) return activeStore;
  progress?.("Checking art version…");
  const r = await fetch(PACK_META, { cache: "no-store" });
  if (!r.ok) throw new Error(`sprite package manifest: HTTP ${r.status}`);
  const meta = await r.json() as PackMeta;
  if (!/^[0-9a-f]{64}$/.test(meta.hash) || !Number.isSafeInteger(meta.bytes)) throw new Error("invalid sprite package manifest");
  const blob = await loadBlob(meta, progress);
  try {
    if (blob.size !== meta.bytes) throw new Error("sprite package size mismatch");
    progress?.("Checking art package…");
    const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
    const hash = [...new Uint8Array(digest)].map((n) => n.toString(16).padStart(2, "0")).join("");
    if (hash !== meta.hash) throw new Error("sprite package checksum mismatch");
    const header = new DataView(await blob.slice(0, 8).arrayBuffer());
    if (String.fromCharCode(...new Uint8Array(header.buffer, 0, 4)) !== "PDSP") throw new Error("invalid sprite package header");
    const indexEnd = 8 + header.getUint32(4, true);
    if (indexEnd > blob.size) throw new Error("invalid sprite package index length");
    const index = JSON.parse(await blob.slice(8, indexEnd).text()) as PackRow[];
    const rows = new Map<string, [number, number]>();
    for (const [path, offset, length] of index) {
      if (typeof path !== "string" || !Number.isSafeInteger(offset) || !Number.isSafeInteger(length)
        || offset < 0 || length < 1 || indexEnd + offset + length > blob.size || rows.has(path)) {
        throw new Error("invalid sprite package index entry");
      }
      rows.set(path, [offset, length]);
    }
    if (rows.size !== meta.files) throw new Error("sprite package file count mismatch");
    activeStore = new SpriteStore(blob, rows, indexEnd);
    return activeStore;
  } catch (error) {
    // A damaged on-disk copy must not make every later reload fail the same
    // way. The loading screen's Retry can then fetch a clean package.
    try { await (await caches.open(CACHE_NAME)).delete(`/sprites.pack?v=${meta.hash}`); } catch { /* unavailable */ }
    throw error;
  }
}
